import { useState } from 'react';
import appConfig from '../../../app-config';
import { cognitoSignUp } from '../../../authentication/cognito';
import Button from 'react-bootstrap/Button';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import parse from 'html-react-parser';
import { Dialog, ButtonVariant, FeedbackType, FormControlType, PasswordPeeker, Variant } from '../..';
import * as formik from 'formik';
import * as yup from 'yup';

import '../login.scss';

export default function FormCreateAccount({ onNotifyEvent, onSwitchToLogin }) {

    const [showDialog, setShowDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('');
    const [dialogContent, setDialogContent] = useState('');
    const [submitButtonEnabled, setSubmitButtonEnabled] = useState(true);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

    const handleSwitchToLogin = (e) => {
        onSwitchToLogin(e);
    }

    const handleCreateAccount = (e) => {
        setSubmitButtonEnabled(false);

        cognitoSignUp(e.firstName, e.lastName, e.email, e.password).then((result) => {
            handleSwitchToLogin({
                firstName: e.firstName,
                lastName: e.lastName,
                email: e.email,
                password: e.password,
            });
            onNotifyEvent({
                email: e.email,
                notificationTitle: 'Success',
                notificationMessage: result,
                notificationType: Variant.SUCCESS,
            });
        }).catch((error) => {
            setSubmitButtonEnabled(true);

            onNotifyEvent({
                notificationTitle: 'Error',
                notificationMessage: error,
                notificationType: Variant.DANGER,
            });
        });
    }

    const onDialogOK = () => {
        setDialogTitle('');
        setDialogContent('');
        setShowDialog(false);
    }

    const onPasswordPeekerChanged = (e) => {
        setPasswordVisible(e.passwordVisible);
    }

    const onConfirmPasswordPeekerChanged = (e) => {
        setConfirmPasswordVisible(e.passwordVisible);
    }

    const { Formik } = formik;

    const schema = yup.object().shape({
        firstName: yup.string().required('First name is required'),
        lastName: yup.string().required('Last name is required'),
        email: yup.string().matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,}$/i, 'Invalid email format').required('Email address is required'),
        password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
        confirmPassword: yup.string().required('Confirm password required').oneOf([yup.ref('password')], 'Passwords must match'),
    });

    const initialValues = {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
    }

    return (
        <div>
            <div className={'app-login-header'}>
                <div className={'app-title'}>{appConfig.app.title}</div>
                <div>{'Create account'}</div>
            </div>

            <Dialog
                show={showDialog}
                title={dialogTitle}
                onOK={onDialogOK}
            >
                {parse(dialogContent)}
            </Dialog>

            <Formik
                validationSchema={schema}
                initialValues={initialValues}
                onSubmit={handleCreateAccount}
            >

                {({ handleSubmit, handleChange, values, touched, errors }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Row>
                            <Form.Group as={Col} size='sm'>
                                <FloatingLabel
                                    label='First name'
                                    className='mb-3'
                                >
                                    <Form.Control
                                        type={FormControlType.TEXT}
                                        name='firstName'
                                        placeholder='First name'
                                        value={values.firstName}
                                        onChange={handleChange}
                                        isValid={touched.firstName && !errors.firstName}
                                        isInvalid={touched.firstName && !!errors.firstName}
                                    />
                                    <Form.Control.Feedback></Form.Control.Feedback>
                                    <Form.Control.Feedback type={FeedbackType.INVALID}>{errors.firstName}</Form.Control.Feedback>
                                </FloatingLabel>
                            </Form.Group>
                        </Row>

                        <Row>
                            <Form.Group as={Col} size='sm'>
                                <FloatingLabel
                                    label='Last name'
                                    className='mb-3'
                                >
                                    <Form.Control
                                        type={FormControlType.TEXT}
                                        name='lastName'
                                        placeholder='Last name'
                                        value={values.lastName}
                                        onChange={handleChange}
                                        isValid={touched.lastName && !errors.lastName}
                                        isInvalid={touched.lastName && !!errors.lastName}
                                    />
                                    <Form.Control.Feedback></Form.Control.Feedback>
                                    <Form.Control.Feedback type={FeedbackType.INVALID}>{errors.lastName}</Form.Control.Feedback>
                                </FloatingLabel>
                            </Form.Group>
                        </Row>

                        <Row>
                            <Form.Group as={Col} size='sm'>
                                <FloatingLabel
                                    label='Email address'
                                    className='mb-3'
                                >
                                    <Form.Control
                                        type={FormControlType.EMAIL}
                                        name='email'
                                        placeholder='name@example.com'
                                        value={values.email}
                                        onChange={handleChange}
                                        isValid={touched.email && !errors.email}
                                        isInvalid={touched.email && !!errors.email}
                                    />
                                    <Form.Control.Feedback></Form.Control.Feedback>
                                    <Form.Control.Feedback type={FeedbackType.INVALID}>{errors.email}</Form.Control.Feedback>
                                </FloatingLabel>
                            </Form.Group>
                        </Row>

                        <Row>
                            <Form.Group as={Col} size='sm'>
                                <FloatingLabel
                                    label='Password'
                                    className='mb-3'
                                >
                                    <Form.Control
                                        type={passwordVisible ? FormControlType.TEXT : FormControlType.PASSWORD}
                                        className='app-password-input-box'
                                        name='password'
                                        placeholder='Password'
                                        value={values.password}
                                        onChange={handleChange}
                                        isValid={touched.email && !errors.password}
                                        isInvalid={touched.email && !!errors.password}
                                    />
                                    <PasswordPeeker onChange={onPasswordPeekerChanged} passwordVisible={passwordVisible} />
                                    <Form.Control.Feedback></Form.Control.Feedback>
                                    <Form.Control.Feedback type={FeedbackType.INVALID}>{errors.password}</Form.Control.Feedback>
                                </FloatingLabel>
                            </Form.Group>
                        </Row>

                        <Row>
                            <Form.Group as={Col} size='sm'>
                                <FloatingLabel
                                    label='Confirm password'
                                    className='mb-3'
                                >
                                    <Form.Control
                                        type={confirmPasswordVisible ? FormControlType.TEXT : FormControlType.PASSWORD}
                                        className='app-password-input-box'
                                        name='confirmPassword'
                                        placeholder='Confirm password'
                                        value={values.confirmPassword}
                                        onChange={handleChange}
                                        isValid={touched.email && !errors.confirmPassword}
                                        isInvalid={touched.email && !!errors.confirmPassword}
                                    />
                                    <PasswordPeeker onChange={onConfirmPasswordPeekerChanged} passwordVisible={confirmPasswordVisible} />
                                    <Form.Control.Feedback></Form.Control.Feedback>
                                    <Form.Control.Feedback type={FeedbackType.INVALID}>{errors.confirmPassword}</Form.Control.Feedback>
                                </FloatingLabel>
                            </Form.Group>
                        </Row>

                        <Row className='app-form-button-row'>
                            <Button
                                variant={ButtonVariant.SUCCESS}
                                type={FormControlType.SUBMIT}
                                disabled={!submitButtonEnabled}
                            >
                                Create an account
                            </Button>
                        </Row>

                        <Row className='app-form-button-link-row'>
                            <span>Already have an account?
                                <Button
                                    className='app-button-link'
                                    variant={ButtonVariant.LINK}
                                    onClick={handleSwitchToLogin}>
                                    Login
                                </Button>
                            </span>
                        </Row>
                    </Form>
                )}
            </Formik>
        </div>
    );
}